import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import {
  Search, Filter, ChevronDown, MoreVertical, Shield,
  AlertCircle, Clock, Phone, MapPin, Calendar,
  Download, Trash2, Edit, Eye, X, CheckCircle,
  User, MessageSquare, Star, Building2,
  Siren, DollarSign, UserCheck, FileText, Timer,
  Zap, Bell, Settings, ArrowUpDown, InfoIcon, Loader2
} from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/badge";
import { Alert, AlertDescription } from "../../components/ui/alert";
import AdminHeader from "../../Headers/AdminHeader";
import SharedFooter3 from "../../Footer/SharedFooter3";
import { fetchEmergencyContracts, updateContract } from "../../redux/Slices/contractSlice";
import { ContractStatusEnum } from "../../modules/shared/model/contract.enum";
import { RootState } from "../../redux/store";

const EmergencyServices = () => {
  const dispatch = useDispatch();
  const { 
    emergencyContracts, 
    emergencyContractsLoading, 
    emergencyContractsTotalCount, 
    emergencyContractsError 
  } = useSelector((state: RootState) => state.contracts);
  
  // State management
  const [searchQuery, setSearchQuery] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [showRequestDetails, setShowRequestDetails] = useState(null);
  const [sortBy, setSortBy] = useState("newest");
  const [currentPage, setCurrentPage] = useState(1);
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);

  // Load emergency contracts on component mount and when filters change
  useEffect(() => {
    const queryParams: Record<string, string | number | boolean> = {
      page: currentPage,
      limit: 10,
    };

    if (searchQuery) queryParams.search = searchQuery;
    if (selectedCategory !== "all") queryParams.type = selectedCategory;
    if (selectedStatus !== "all") queryParams.status = selectedStatus;
    if (sortBy !== "newest") queryParams.sortBy = sortBy;

    dispatch(fetchEmergencyContracts(queryParams) as any);
  }, [dispatch, currentPage, searchQuery, selectedCategory, selectedStatus, sortBy]);

  // Handle search with debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const queryParams: Record<string, string | number | boolean> = {
        page: 1,
        limit: 10,
      };

      if (searchQuery) queryParams.search = searchQuery;
      if (selectedCategory !== "all") queryParams.type = selectedCategory;
      if (selectedStatus !== "all") queryParams.status = selectedStatus;
      if (sortBy !== "newest") queryParams.sortBy = sortBy;

      dispatch(fetchEmergencyContracts(queryParams) as any);
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, dispatch, selectedCategory, selectedStatus, sortBy]);

  // Handle contract status updates
  const handleStatusUpdate = async (contractId: string, newStatus: ContractStatusEnum) => {
    try {
      setUpdatingStatus(contractId);
      await dispatch(updateContract({ 
        id: contractId, 
        data: { 
          status: { id: newStatus } 
        } 
      }) as any);
      // Refresh the emergency contracts list
      dispatch(fetchEmergencyContracts({ page: currentPage, limit: 10 }) as any);
    } catch (error) {
      console.error("Failed to update contract status:", error);
    } finally {
      setUpdatingStatus(null);
    }
  };

  // Handle contact actions
  const handleContactCustomer = (phone: string, name: string) => {
    // Open phone dialer or copy phone number
    if (navigator.userAgent.match(/iPhone|iPad|iPod|Android/i)) {
      window.open(`tel:${phone}`);
    } else {
      navigator.clipboard.writeText(phone);
      alert(`Phone number ${phone} copied to clipboard for ${name}`);
    }
  };

  const handleContactProvider = (phone: string, name: string) => {
    // Open phone dialer or copy phone number
    if (navigator.userAgent.match(/iPhone|iPad|iPod|Android/i)) {
      window.open(`tel:${phone}`);
    } else {
      navigator.clipboard.writeText(phone);
      alert(`Phone number ${phone} copied to clipboard for ${name}`);
    }
  };

  // Get status display info
  const getStatusInfo = (statusId: number) => {
    switch (statusId) {
      case ContractStatusEnum.Active:
        return { text: "Active", color: "bg-green-100 text-green-700", dot: "bg-green-500" };
      case ContractStatusEnum.Pending:
        return { text: "Pending", color: "bg-yellow-100 text-yellow-700", dot: "bg-yellow-500" };
      case ContractStatusEnum.Suspended:
        return { text: "Suspended", color: "bg-red-100 text-red-700", dot: "bg-red-500" };
      case ContractStatusEnum.Deleted:
        return { text: "Deleted", color: "bg-gray-100 text-gray-700", dot: "bg-gray-500" };
      case ContractStatusEnum.Completed:
        return { text: "Completed", color: "bg-blue-100 text-blue-700", dot: "bg-blue-500" };
      default:
        return { text: "Unknown", color: "bg-gray-100 text-gray-700", dot: "bg-gray-500" };
    }
  };

  // Emergency Request Details Modal Component
  const RequestDetailsModal = ({ request, isOpen, onClose }) => {
    if (!request) return null;

    const statusInfo = getStatusInfo(Number(request.status.id));
    const professional = request.professional;
    const client = request.client;

    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        >
          <motion.div
            initial={{ scale: 0.95 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0.95 }}
            className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b sticky top-0 bg-white"  style={{ textAlign: "left" }}>
              <div className="flex justify-between items-start">
                <div>
                  <div className="flex items-center gap-3">
                    <Badge variant={undefined} className={statusInfo.color}>
                      {statusInfo.text}
                    </Badge>
                    <Badge variant="outline" className="border-red-200 text-red-700">
                      EMERGENCY
                    </Badge>
                  </div>
                  <h2 className="text-xl font-semibold mt-2">{request.title}</h2>
                  <p className="text-sm text-gray-500">ID: {request.id}</p>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-gray-100 rounded-full"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6"  style={{ textAlign: "left" }}>
              {/* Emergency Details */}
              <div className="space-y-4">
                <h3 className="font-medium">Emergency Details</h3>
                <Alert className="bg-red-50 border-red-200">
                  <Siren className="h-4 w-4 text-red-600" />
                  <AlertDescription className="text-red-700">
                    {request.description}
                  </AlertDescription>
                </Alert>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-sm text-gray-500">Created</span>
                    <div className="flex items-center gap-2 mt-1">
                      <Clock className="h-4 w-4 text-gray-400" />
                      <span>{new Date(request.createdAt).toLocaleString()}</span>
                    </div>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Amount</span>
                    <div className="flex items-center gap-2 mt-1">
                      <DollarSign className="h-4 w-4 text-gray-400" />
                      <span>${request.amount}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Customer Information */}
              <div className="space-y-4">
                <h3 className="font-medium">Customer Information</h3>
                <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-sm text-gray-500">Name</span>
                      <p className="font-medium">{client?.firstName} {client?.lastName}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Phone</span>
                      <p className="font-medium">{client?.phone}</p>
                    </div>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Address</span>
                    <div className="flex items-center gap-2 mt-1">
                      <MapPin className="h-4 w-4 text-gray-400" />
                      <p className="font-medium">{client?.address}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Assigned Provider */}
              {professional && (
                <div className="space-y-4">
                  <h3 className="font-medium">Assigned Provider</h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <User className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                          <h4 className="font-medium">{professional.firstName} {professional.lastName}</h4>
                          <div className="flex items-center gap-2 text-sm text-gray-500">
                            <Star className="h-4 w-4 text-yellow-400 fill-current" />
                            <span>{professional.averageRating || "N/A"}</span>
                            <span>•</span>
                            <span>{professional.reviewsCount || 0} reviews</span>
                          </div>
                        </div>
                      </div>
                      <Badge variant="secondary" className="bg-green-100 text-green-700">
                        Verified Provider
                      </Badge>
                    </div>
                    <div className="mt-4 grid grid-cols-2 gap-4">
                      <div>
                        <span className="text-sm text-gray-500">Phone</span>
                        <div className="flex items-center gap-2 mt-1">
                          <Phone className="h-4 w-4 text-gray-400" />
                          <span>{professional.phone}</span>
                        </div>
                      </div>
                      <div>
                        <span className="text-sm text-gray-500">Email</span>
                        <div className="flex items-center gap-2 mt-1">
                          <User className="h-4 w-4 text-gray-400" />
                          <span>{professional.email}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Activity Log */}
              <div className="space-y-4">
                <h3 className="font-medium">Activity Log</h3>
                <div className="space-y-4">
                  {request.notes && request.notes.length > 0 ? (
                    request.notes.map((note, index) => (
                      <div key={index} className="flex gap-3">
                        <div className={`p-2 rounded-lg ${
                          note.type === "system" ? "bg-gray-100" :
                          note.type === "admin" ? "bg-blue-100" :
                          "bg-green-100"
                        }`}>
                          {note.type === "system" ? (
                            <Settings className="h-4 w-4 text-gray-600" />
                          ) : note.type === "admin" ? (
                            <Shield className="h-4 w-4 text-blue-600" />
                          ) : (
                            <User className="h-4 w-4 text-green-600" />
                          )}
                        </div>
                        <div>
                          <div className="text-sm text-gray-500">
                            {new Date(note.time).toLocaleString()}
                          </div>
                          <p className="mt-1">{note.text}</p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-4 text-gray-500">
                      <InfoIcon className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                      <p>No activity log available</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Admin Actions */}
              <div className="space-y-4 pt-4 border-t">
                <h3 className="font-medium">Admin Actions</h3>
                <div className="flex flex-wrap gap-3">
                  <Button 
                    variant="outline" 
                    className="flex items-center gap-2"
                    onClick={() => handleContactCustomer(client?.phone || "", `${client?.firstName} ${client?.lastName}`)}
                  >
                    <Phone className="h-4 w-4" />
                    Contact Customer
                  </Button>
                  <Button variant="outline" className="flex items-center gap-2">
                    <UserCheck className="h-4 w-4" />
                    Reassign Provider
                  </Button>
                  {request.status.id === ContractStatusEnum.Active ? (
                    <Button 
                      variant="outline" 
                      className="flex items-center gap-2 text-yellow-600"
                      onClick={() => handleStatusUpdate(request.id, ContractStatusEnum.Suspended)}
                      disabled={updatingStatus === request.id}
                    >
                      {updatingStatus === request.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <AlertCircle className="h-4 w-4" />
                      )}
                      Suspend Contract
                    </Button>
                  ) : request.status.id === ContractStatusEnum.Suspended ? (
                    <Button 
                      variant="outline" 
                      className="flex items-center gap-2 text-green-600"
                      onClick={() => handleStatusUpdate(request.id, ContractStatusEnum.Active)}
                      disabled={updatingStatus === request.id}
                    >
                      {updatingStatus === request.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <CheckCircle className="h-4 w-4" />
                      )}
                      Activate Contract
                    </Button>
                  ) : null}
                  <Button 
                    variant="outline" 
                    className="flex items-center gap-2 text-red-600"
                    onClick={() => handleStatusUpdate(request.id, ContractStatusEnum.Deleted)}
                    disabled={updatingStatus === request.id}
                  >
                    {updatingStatus === request.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <X className="h-4 w-4" />
                    )}
                    Delete Contract
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    );
  };

  // Emergency Request Card Component
  const EmergencyRequestCard = ({ request }) => {
    const statusInfo = getStatusInfo(Number(request.status.id));
    
    return (
      <Card className="hover:shadow-md transition-shadow"  style={{ textAlign: "left" }}>
        <div className="p-6 space-y-4"  style={{ textAlign: "left" }}>
          {/* Header */}
          <div className="flex justify-between items-start">
            <div>
              <div className="flex items-center gap-2">
                <Badge variant={undefined} className={statusInfo.color}>
                  {statusInfo.text}
                </Badge>
                <Badge variant="outline" className="border-red-200 text-red-700">
                  EMERGENCY
                </Badge>
              </div>
              <h3 className="font-semibold mt-2">{request.title}</h3>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowRequestDetails(request)}
              className="text-gray-600 hover:text-gray-900"
            >
              <Eye className="h-4 w-4" />
            </Button>
          </div>

          {/* Description */}
          <Alert className="bg-red-50 border-red-200">
            <Siren className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-700">
              {request.description}
            </AlertDescription>
          </Alert>

          {/* Location and Time */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <span className="text-sm text-gray-500">Amount</span>
              <div className="flex items-center gap-2 mt-1">
                <DollarSign className="h-4 w-4 text-gray-400" />
                <span>${request.amount}</span>
              </div>
            </div>
            <div>
              <span className="text-sm text-gray-500">Created</span>
              <div className="flex items-center gap-2 mt-1">
                <Clock className="h-4 w-4 text-gray-400" />
                <span>{new Date(request.createdAt).toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* Customer Info */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Customer</h4>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 bg-gray-100 rounded-full flex items-center justify-center">
                  <User className="h-4 w-4 text-gray-600" />
                </div>
                <div>
                  <p className="font-medium">{request.client?.firstName} {request.client?.lastName}</p>
                  <p className="text-sm text-gray-500">{request.client?.phone}</p>
                </div>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                className="flex items-center gap-2"
                onClick={() => handleContactCustomer(request.client?.phone || "", `${request.client?.firstName} ${request.client?.lastName}`)}
              >
                <Phone className="h-4 w-4" />
                Contact
              </Button>
            </div>
          </div>

          {/* Provider Info */}
          {request.professional && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Assigned Provider</h4>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <User className="h-4 w-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium">{request.professional.firstName} {request.professional.lastName}</p>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Star className="h-4 w-4 text-yellow-400 fill-current" />
                      <span>{request.professional.averageRating || "N/A"}</span>
                      <span>•</span>
                      <span>{request.professional.reviewsCount || 0} reviews</span>
                    </div>
                  </div>
                </div>
                <Badge variant="secondary" className="bg-green-100 text-green-700">
                  Verified
                </Badge>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="pt-4 border-t flex justify-between items-center">
            <div className="flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-gray-400" />
              <span className="text-sm text-gray-600">Amount: ${request.amount}</span>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm">
                Reassign
              </Button>
              <Button size="sm" onClick={() => setShowRequestDetails(request)}>
                View Details
              </Button>
            </div>
          </div>
        </div>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col"  style={{ textAlign: "left" }}>
      <AdminHeader />
      
      <main className="flex-1 lg:pl-64 pt-16">
        <div className="p-6">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Emergency Services</h1>
                <p className="text-gray-600 mt-1">Manage and monitor emergency service requests</p>
              </div>
              <div className="flex gap-3">
                <Button variant="outline" className="flex items-center gap-2">
                  <Download className="h-4 w-4" />
                  Export Report
                </Button>
              </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500">Total Emergencies</p>
                    <p className="text-2xl font-semibold">{emergencyContractsTotalCount || 0}</p>
                  </div>
                  <div className="p-2 bg-red-100 rounded-lg">
                    <Siren className="h-5 w-5 text-red-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500">Active Emergencies</p>
                    <p className="text-2xl font-semibold">
                      {emergencyContracts.filter(c => c.status.id === ContractStatusEnum.Active).length}
                    </p>
                  </div>
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Timer className="h-5 w-5 text-blue-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500">Pending Emergencies</p>
                    <p className="text-2xl font-semibold">
                      {emergencyContracts.filter(c => c.status.id === ContractStatusEnum.Pending).length}
                    </p>
                  </div>
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <AlertCircle className="h-5 w-5 text-yellow-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500">Total Revenue</p>
                    <p className="text-2xl font-semibold">
                      ${emergencyContracts.reduce((sum, c) => sum + (c.amount || 0), 0).toLocaleString()}
                    </p>
                  </div>
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <DollarSign className="h-5 w-5 text-purple-600" />
                  </div>
                </div>
              </Card>
            </div>

            {/* Search and Filters */}
            <Card className="p-4">
              <div className="flex flex-col sm:flex-row justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="relative flex-1 sm:min-w-[300px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search emergency requests..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border rounded-lg"
                    />
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => setShowFilters(!showFilters)}
                    className="flex items-center gap-2"
                  >
                    <Filter className="h-5 w-5" />
                    Filters
                  </Button>
                </div>
                
                <div className="flex items-center gap-4">
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="px-4 py-2 border rounded-lg"
                  >
                    <option value="all">All Categories</option>
                    <option value="plumbing">Plumbing Emergency</option>
                    <option value="electrical">Electrical Emergency</option>
                    <option value="hvac">HVAC Emergency</option>
                    <option value="locksmith">Locksmith Emergency</option>
                    <option value="appliance">Appliance Repair</option>
                    <option value="roofing">Roofing Emergency</option>
                    <option value="heating">Heating Emergency</option>
                    <option value="cooling">Cooling Emergency</option>
                    <option value="water">Water Damage</option>
                    <option value="gas">Gas Emergency</option>
                    <option value="security">Security Emergency</option>
                    <option value="medical">Medical Emergency</option>
                    <option value="fire">Fire Emergency</option>
                    <option value="other">Other Emergency</option>
                  </select>

                  <select
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    className="px-4 py-2 border rounded-lg"
                  >
                    <option value="all">All Status</option>
                    <option value="1">Pending</option>
                    <option value="2">Active</option>
                    <option value="3">Cancelled</option>
                    <option value="4">Completed</option>
                    <option value="5">Archived</option>
                    <option value="6">Suspended</option>
                    <option value="7">Deleted</option>
                    <option value="8">Paused</option>
                    <option value="9">Expired</option>
                    <option value="10">Rejected</option>
                    <option value="11">Accepted</option>
                  </select>
                </div>
              </div>
            </Card>

            {/* Emergency Requests Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {emergencyContractsLoading ? (
                <div className="col-span-2 flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                  <span className="ml-2 text-gray-500">Loading emergency contracts...</span>
                </div>
              ) : emergencyContracts.length > 0 ? (
                emergencyContracts.map(request => (
                  <EmergencyRequestCard key={request.id} request={request} />
                ))
              ) : (
                <div className="col-span-2 flex items-center justify-center py-12">
                  <div className="text-center">
                    <Siren className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Emergency Contracts</h3>
                    <p className="text-gray-500">No emergency contracts found matching your criteria.</p>
                  </div>
                </div>
              )}
            </div>

            {/* Pagination */}
            {emergencyContractsTotalCount && emergencyContractsTotalCount > 10 && (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-700">
                    Showing {emergencyContracts.length} of {emergencyContractsTotalCount} emergency contracts
                  </span>
                  {emergencyContractsTotalCount && (
                    <span className="text-sm text-gray-500">
                      (Page {currentPage} of {Math.ceil(emergencyContractsTotalCount / 10)})
                    </span>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1 || emergencyContractsLoading}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={emergencyContractsLoading || (emergencyContractsTotalCount && currentPage >= Math.ceil(emergencyContractsTotalCount / 10))}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Request Details Modal */}
      <RequestDetailsModal
        request={showRequestDetails}
        isOpen={!!showRequestDetails}
        onClose={() => setShowRequestDetails(null)}
      />
      <br></br>
      <br></br>
      <br></br>
      <br></br>
      <br></br>

      {/* <SharedFooter3 /> */}
    </div>
  );
};

export default EmergencyServices;